<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields['title'] }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div>
          <label class="label-input">Section Name</label>
          <v-row>
            <v-col cols="12" md="8">
              <div>
                <v-text-field v-model="fields['title']" label="Name" />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox small v-model="fields['enableAnchor']" label="Enable Anchor" />
            </v-col>
          </v-row>
        </div>
        <div>
          <label class="label-input">Title</label>
          <ckeditor :editor="editor" v-model="fields.reviewHeader" :config="editorConfig" />
        </div>
        <div class="mt-5">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="tagsShow" style="width: fit-content">
            <span>Tags</span>
            <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields.tag.show" id="tagsShow" />
          </label>

          <div class="group-item">
            <v-row class="mt-1 mb-2">
              <v-col cols="12" md="12">
                <v-text-field
                  type="number"
                  v-model.number="fields.tag.borderRadius"
                  label="Tag Border Radius"
                  min="0"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v >= 0 || 'Tag Border Radius must be greater than or equal 0']"
                />
              </v-col>
            </v-row>
            <v-divider class="py-2" />
            <template v-if="tagsEditing">
              <div class="d-flex flex-column align-start flex-wrap mt-4" style="gap: 24px">
                <div
                  v-for="(item, i) in fields.tag.options"
                  :key="'tag-edit-' + i"
                  class="d-flex align-start"
                  style="gap: 8px; width: 100%"
                >
                  <v-text-field
                    v-model="item.text"
                    dense
                    hide-details
                    label="Label"
                    @input="syncTagValueFromLabel(item)"
                  />
                  <v-btn icon small class="mt-1 flex-shrink-0" @click="handleRemoveTag(i)">
                    <v-icon small color="#ff6c6c">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
              <template v-if="tagsEditing">
                <div class="d-flex align-center flex-wrap justify-end mt-4" style="gap: 6px">
                  <v-btn small text @click="cancelTagEditing">Cancel</v-btn>
                  <v-btn small color="primary" depressed @click="finishEditTags">Save</v-btn>
                </div>
              </template>
            </template>
            <template v-else>
              <div class="d-flex align-start flex-wrap" style="gap: 8px">
                <v-text-field
                  ref="tagComposerInput"
                  v-model="newTagLabel"
                  dense
                  label="New tag"
                  placeholder="e.g. Verified buyer"
                  small
                  hide-details
                  @keyup.enter="confirmAddTag"
                />
                <v-btn color="primary" depressed small class="flex-shrink-0" @click="confirmAddTag">
                  <v-icon left small>mdi-plus</v-icon>
                  Add
                </v-btn>
                <v-btn small outlined color="primary" @click="startTagEditing">
                  <v-icon left small>mdi-pencil-outline</v-icon>
                  Edit
                </v-btn>
              </div>
              <div v-if="fields.tag.options.length" class="d-flex flex-wrap align-center mt-5" style="gap: 8px">
                <v-chip
                  v-for="(item, i) in fields.tag.options"
                  :key="'tag-chip-' + i"
                  small
                  label
                  close
                  @click:close="handleRemoveTag(i)"
                >
                  {{ item.text }}
                </v-chip>
              </div>
            </template>
          </div>
        </div>
        <div class="mt-5">
          <label class="label-input">Button</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="fields.button.readMoreText" label="Read More Text" dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="fields.button.showLessText" label="Show Less Text" dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="fields.button.loadMoreText" label="Load More Text" dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.button.textLineClamp"
                  label="Text Line Limit"
                  min="1"
                  :rules="[v => v >= 1 || 'Text Line Limit must be greater than or equal 1']"
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.button.pageSize"
                  label="Page Size"
                  min="1"
                  :rules="[v => v >= 1 || 'Page Size must be greater than or equal 1']"
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.button.borderRadius"
                  label="Border Radius"
                  min="0"
                  :rules="[v => v >= 0 || 'Border Radius must be greater than or equal 0']"
                  dense
                  hide-details
                  suffix="px"
                />
              </v-col>
            </v-row>
          </div>
        </div>
        <div class="mt-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input" style="margin-bottom: 10px">Reviews ({{ fields.reviewsList.length }})</label>
            <v-btn small color="primary" @click="handleAddReview">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Review</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  type="number"
                  :rules="[
                    v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                    v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                  ]"
                  min="1"
                  v-model="fields.itemsPerRow.mobile"
                  label="Items Per Row (Mobile)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  type="number"
                  :rules="[
                    v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                    v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                  ]"
                  min="1"
                  v-model="fields.itemsPerRow.desktop"
                  label="Items Per Row (Desktop)"
                />
              </v-col>
            </v-row>
            <p class="text-caption mb-4">
              Effective items per row are automatically limited by current review count ({{
                this.fields.reviewsList.length
              }})
            </p>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.avatar.desktopSize"
                  label="Avatar Size (Desktop)"
                  min="0"
                  suffix="px"
                  :rules="[v => v >= 0 || 'Avatar Size must be greater than or equal 0']"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.avatar.mobileSize"
                  label="Avatar Size (Mobile)"
                  min="0"
                  suffix="px"
                  :rules="[v => v >= 0 || 'Avatar Size must be greater than or equal 0']"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.avatar.showBorder" label="Show Avatar Border" />
              </v-col>
            </v-row>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, i) in fields.reviewsList" :key="i">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <div class="reviewer-header">
                      <div class="reviewer-avatar">
                        <img :src="item.avatar" alt="" onerror="this.style.display='none'" />
                      </div>
                      <div class="reviewer-info">
                        <span class="reviewer-name line-clamp-1">{{ item.title || '' }}</span>
                      </div>
                      <v-chip style="text-transform: capitalize" v-if="item && item.tag" small label>{{
                        item && item.tag.replace(/-/g, ' ')
                      }}</v-chip>
                    </div>
                    <v-btn icon @click.stop="handleRemoveReview(i)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="group-item">
                    <v-text-field v-model="item.title" label="Review Title" />
                    <v-textarea
                      class="mt-2 mb-4"
                      v-model="item.content"
                      label="Review Content"
                      rows="4"
                      outlined
                      hide-details
                    />
                    <v-select
                      label="Tag"
                      placeholder="Select a tag"
                      :items="fields.tag.options"
                      item-text="text"
                      item-value="value"
                      v-model="item.tag"
                    />
                    <v-text-field v-model="item.name" label="Reviewer Name" />
                    <div>
                      <label>Avatar Image</label>
                      <UploadImage
                        @callback="obj => handleUploadAvatar(obj, i)"
                        :image="{ imageSrc: item.avatar }"
                        :height="`150`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveAvatar(i)"
                      />
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Spacing" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Spacing" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.primaryColor"
                    name="primaryColor"
                    label="Primary Color"
                    default-color="#14a16c"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Tag Colors</div>
              <v-row>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.tag.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#ebeced"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.tag.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.tag.activeTextColor"
                    name="activeTextColor"
                    label="Active Text Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Review Lists Colors</div>
              <v-row>
                <v-col cols="12" md="12">
                  <ColorPicker
                    v-model="fields.colors.reviewsListBackgroundColor"
                    name="reviewsListBackgroundColor"
                    label="Background Color"
                    default-color="#f5f6f7"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Button Colors</div>
              <v-row>
                <v-col cols="12" md="12">
                  <ColorPicker
                    v-model="fields.button.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import productReviewsJson from '@/assets/json/product-reviews.json';
import ColorPicker from './common/color-picker.vue';

export default {
  name: 'ReviewGridSectionEditor04',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Review Grid Section 04',
        reviewHeader: '',
        reviewsList: [],
        topPadding: true,
        bottomPadding: true,
        colors: {
          backgroundColor: '#ffffff',
          textColor: '#000000',
          primaryColor: '#14a16c',
          reviewsListBackgroundColor: '#f5f6f7',
        },
        button: {
          readMoreText: 'read more',
          showLessText: 'show less',
          loadMoreText: 'Load More',
          textColor: '#ffffff',
          borderRadius: 24,
          textLineClamp: 2,
          pageSize: 4,
        },
        tag: {
          backgroundColor: '#ebeced',
          textColor: '#000000',
          activeTextColor: '#ffffff',
          borderRadius: 0,
          options: [],
          show: true,
        },
        itemsPerRow: {
          mobile: 1,
          desktop: 2,
        },
        avatar: {
          desktopSize: 200,
          mobileSize: 150,
          showBorder: true,
        },
      },
      tagsEditing: false,
      newTagLabel: '',
      tagOptionsBackup: null,
    };
  },
  methods: {
    ensureTagOptions() {
      if (!this.fields.tag) return;
      if (!Array.isArray(this.fields.tag.options)) {
        this.$set(this.fields.tag, 'options', []);
      }
    },
    slugifyTagValue(text) {
      return String(text || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-');
    },
    syncTagValueFromLabel(item) {
      if (item && typeof item === 'object') {
        item.value = this.slugifyTagValue(item.text);
      }
    },
    startTagEditing() {
      this.tagOptionsBackup = cloneDeepObject(this.fields.tag.options);
      this.tagsEditing = true;
    },
    cancelTagEditing() {
      if (this.tagOptionsBackup != null) {
        this.fields.tag.options = cloneDeepObject(this.tagOptionsBackup);
      }
      this.tagOptionsBackup = null;
      this.tagsEditing = false;
    },
    confirmAddTag() {
      const label = (this.newTagLabel || '').trim();
      if (!label) {
        this.$store.dispatch('setMessage', {
          color: 'warning',
          dialog: true,
          content: 'Please enter a tag label.',
        });
        return;
      }
      const existingTag = this.fields.tag.options.find(opt => opt.value === this.slugifyTagValue(label));
      if (existingTag) {
        this.$store.dispatch('setMessage', {
          color: 'warning',
          dialog: true,
          content: 'Tag already exists.',
        });
        return;
      }
      this.fields.tag.options.push({
        text: label,
        value: this.slugifyTagValue(label),
      });
      this.newTagLabel = '';
      this.$nextTick(() => {
        const el = this.$refs.tagComposerInput;
        if (el && typeof el.focus === 'function') {
          el.focus();
        }
      });
    },
    finishEditTags() {
      const options = this.fields.tag.options;
      const hasDuplicate = new Set(options.map(opt => opt.value)).size !== options.length;
      if (hasDuplicate) {
        this.$store.dispatch('setMessage', {
          color: 'warning',
          dialog: true,
          content: 'Tags must be unique.',
        });
        return;
      }
      const emptyIndex = options.findIndex(opt => !String(opt.text || '').trim());
      if (emptyIndex !== -1) {
        this.$store.dispatch('setMessage', {
          color: 'warning',
          dialog: true,
          content: 'Every tag must have a label.',
        });
        return;
      }
      options.forEach(opt => {
        opt.text = String(opt.text).trim();
        opt.value = this.slugifyTagValue(opt.text);
      });
      this.tagOptionsBackup = null;
      this.tagsEditing = false;
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionReviewGrid04(this.sectionEditor.section.html, this.fields),
      };
      this.normalizeItemsPerRow();
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },
    handleAddReview() {
      const existingKeys = new Set(this.fields.reviewsList.map(r => `${r.name}-${r.avatar}`));

      const candidates = productReviewsJson.slice(0, 10).filter(item => {
        const key = `${item.fullName || ''}-${item.avatar || ''}`;
        return !existingKeys.has(key);
      });

      if (candidates.length === 0) {
        this.$store.dispatch('setMessage', {
          color: 'warning',
          dialog: true,
          content: 'No more reviews to add!',
        });
        return;
      }

      const randomReview = candidates[Math.floor(Math.random() * candidates.length)];

      this.fields.reviewsList.unshift({
        name: randomReview.fullName || ``,
        title: 'Title',
        content: 'Content',
        avatar: randomReview.avatar || '',
      });
      this.normalizeItemsPerRow();
    },
    handleRemoveReview(index) {
      this.fields.reviewsList.splice(index, 1);
      this.normalizeItemsPerRow();
    },
    handleUploadAvatar(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.reviewsList[index].avatar = obj.imageSrc;
      }
    },
    handleRemoveAvatar(index) {
      this.fields.reviewsList[index].avatar = '';
    },
    handleRemoveTag(index) {
      this.fields.tag.options.splice(index, 1);
    },
    normalizeItemsPerRow() {
      const max = this.maxItemsPerRow;
      const mobile = Number(this.fields.itemsPerRow?.mobile);
      const desktop = Number(this.fields.itemsPerRow?.desktop);

      if (!this.fields.itemsPerRow) {
        this.$set(this.fields, 'itemsPerRow', { mobile: max, desktop: max });
        return;
      }

      const safeMobile = Number.isNaN(mobile) || mobile < 1 ? 1 : mobile;
      const safeDesktop = Number.isNaN(desktop) || desktop < 1 ? 1 : desktop;

      this.fields.itemsPerRow.mobile = Math.min(safeMobile, max);
      this.fields.itemsPerRow.desktop = Math.min(safeDesktop, max);
    },
  },
  computed: {
    maxItemsPerRow() {
      return Math.max(1, this.fields.reviewsList?.length || 0);
    },
  },
  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
      this.ensureTagOptions();
    }
    this.tagsEditing = false;
    this.tagOptionsBackup = null;
    this.newTagLabel = '';
    this.normalizeItemsPerRow();
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
          this.ensureTagOptions();
          this.tagsEditing = false;
          this.tagOptionsBackup = null;
          this.newTagLabel = '';
          this.normalizeItemsPerRow();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.tags-panel {
  background-color: #fafafa;
}

.tag-edit-input {
  max-width: 100%;
}
::v-deep .ck-editor__editable_inline {
  min-height: 150px !important;
}
.reviewer-header {
  display: flex;
  align-items: center;
  gap: 12px;

  .reviewer-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e0e0e0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: relative;
      z-index: 2;
    }

    &::before {
      content: '';
      font-size: 20px;
      color: #999;
      position: absolute;
    }
  }

  .reviewer-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .reviewer-name {
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
